import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, FolderKanban, ListChecks, Plus } from "lucide-react";
import { toast } from "sonner";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useProject } from "./project-context";
import { ProjectCreateDialog } from "./project-create-dialog";
import { useViewerCapabilities } from "@/lib/capabilities";
import { useViewerGuide } from "@/features/onboarding/use-viewer-guide";
import { showOrganizationAccessRequired } from "@/lib/organization-access-errors";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAppNavigate } from "@/lib/use-app-navigate";

export function ProjectSwitcher() {
  const navigate = useAppNavigate();
  const { project, projects, recentProjectIds, isLoading, switchProject, refreshProjects } =
    useProject();

  const [open, setOpen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [visibleProjectCount, setVisibleProjectCount] = useState(10);

  useEffect(() => {
    setVisibleProjectCount(10);
  }, [searchValue]);

  const filteredProjects = useMemo(() => {
    if (!projects) {
      return [];
    }

    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return projects;
    }

    return projects.filter((proj) => proj.name.toLowerCase().includes(query));
  }, [projects, searchValue]);

  const recentProjects = useMemo(() => {
    if (filteredProjects.length === 0) {
      return [];
    }

    const projectById = new Map(filteredProjects.map((proj) => [proj._id, proj]));
    return recentProjectIds
      .map((projectId) => projectById.get(projectId))
      .filter((proj): proj is (typeof filteredProjects)[number] => proj !== undefined);
  }, [filteredProjects, recentProjectIds]);

  const recentProjectIdSet = useMemo(
    () => new Set(recentProjects.map((proj) => proj._id)),
    [recentProjects]
  );

  const remainingProjects = useMemo(
    () => filteredProjects.filter((proj) => !recentProjectIdSet.has(proj._id)),
    [filteredProjects, recentProjectIdSet]
  );

  const visibleProjects = remainingProjects.slice(0, visibleProjectCount);
  const canLoadMoreProjects = remainingProjects.length > visibleProjects.length;

  const capabilities = useViewerCapabilities();
  const viewerGuide = useViewerGuide();
  // Before a plan or grant, explain access with the shared dialog instead of a form that would fail.
  const openCreateDialog = () => {
    if (viewerGuide && !viewerGuide.access.canWrite) {
      showOrganizationAccessRequired(viewerGuide.access.state);
      return;
    }
    setShowCreateDialog(true);
  };

  const handleSelectProject = (projectId: string) => {
    if (projectId === "__create_new__") {
      openCreateDialog();
      setOpen(false);
      return;
    }

    if (projectId === "__view_all__") {
      navigate({ to: "/", title: "Projects" });
      setOpen(false);
      return;
    }

    switchProject(projectId as Id<"projects">, {
      to: "/project",
      title: "Project",
    });
    setOpen(false);
    setSearchValue("");
  };

  const createDialog = (
    <ProjectCreateDialog
      open={showCreateDialog}
      onOpenChange={setShowCreateDialog}
      description="Enter a name for your new project. Projects help you organize related submittals and documents."
      inputLabel="Project Name"
      inputPlaceholder="Project name"
      showDetailsFields
      onCreated={async (projectId) => {
        try {
          const result = await refreshProjects();
          const refreshedProjects = result.data ?? [];
          const hasNewProject = refreshedProjects.some((row) => row._id === projectId);

          if (!hasNewProject) {
            toast.error("Couldn't open the new project yet. Please try again.");
            return;
          }

          switchProject(projectId as Id<"projects">, {
            to: "/project",
            title: "Project",
          });
        } catch (caughtError) {
          notifyError(caughtError, "Couldn't refresh projects. Please try again.");
        }
      }}
    />
  );

  if (isLoading) {
    return (
      <Button
        variant="ghost"
        className="w-[200px] justify-between px-3 text-sm font-semibold text-sidebar-foreground"
        disabled
      >
        <span className="flex min-w-0 items-center gap-2">
          <FolderKanban className="size-4 shrink-0" />
          <span className="truncate">Loading...</span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  if (!projects || projects.length === 0) {
    // Members and reviewers wait to be added; only roles the server lets create projects see this.
    if (!capabilities?.canCreateProjects) return null;
    return (
      <>
        <Button
          variant="ghost"
          className="w-[200px] justify-between px-3 text-sm font-semibold text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          onClick={openCreateDialog}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Plus className="size-4 shrink-0" />
            <span className="truncate">New Project</span>
          </span>
        </Button>
        {createDialog}
      </>
    );
  }

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setSearchValue("");
            setVisibleProjectCount(10);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="w-[200px] justify-between px-3 text-sm font-semibold text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground lg:w-[240px]"
            aria-expanded={open}
            aria-label="Select a project"
          >
            <span className="flex min-w-0 items-center gap-2">
              <FolderKanban className="size-4 shrink-0" />
              <span className="truncate">{project?.name ?? "Select Project"}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Find a project..."
              value={searchValue}
              onValueChange={setSearchValue}
              className="border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 [&[cmdk-input]]:border-none [&[cmdk-input]]:ring-0 [&[cmdk-input]]:focus:ring-0 [&[cmdk-input]]:focus-visible:ring-0"
            />
            <CommandList>
              {recentProjects.length === 0 && visibleProjects.length === 0 ? (
                <CommandEmpty>No projects found.</CommandEmpty>
              ) : null}

              {recentProjects.length > 0 ? (
                <CommandGroup heading="Recent Projects">
                  {recentProjects.map((proj) => (
                    <CommandItem
                      key={proj._id}
                      value={proj._id}
                      onSelect={handleSelectProject}
                      className="cursor-pointer"
                    >
                      <FolderKanban className="size-4 shrink-0" />
                      <span className="flex-1 truncate">{proj.name}</span>
                      {project?._id === proj._id ? <Check className="size-4 shrink-0" /> : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {visibleProjects.length > 0 ? (
                <CommandGroup heading={recentProjects.length > 0 ? "All Projects" : "Projects"}>
                  {visibleProjects.map((proj) => (
                    <CommandItem
                      key={proj._id}
                      value={proj._id}
                      onSelect={handleSelectProject}
                      className="cursor-pointer"
                    >
                      <FolderKanban className="size-4 shrink-0" />
                      <span className="flex-1 truncate">{proj.name}</span>
                      {project?._id === proj._id ? <Check className="size-4 shrink-0" /> : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {canLoadMoreProjects ? (
                <div className="px-2 pb-2 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-center"
                    onClick={() => setVisibleProjectCount((currentCount) => currentCount + 10)}
                  >
                    Show 10 more
                  </Button>
                </div>
              ) : null}

              <CommandSeparator />
              <CommandGroup>
                {capabilities?.canCreateProjects && (
                  <CommandItem
                    value="__create_new__"
                    onSelect={handleSelectProject}
                    className="cursor-pointer text-primary"
                  >
                    <Plus className="size-4" />
                    Create New Project
                  </CommandItem>
                )}
                <CommandItem
                  value="__view_all__"
                  onSelect={handleSelectProject}
                  className="cursor-pointer"
                >
                  <ListChecks className="size-4" />
                  View All Projects
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {createDialog}
    </>
  );
}
